import { Message as TMessage } from '@langchain/langgraph-sdk';
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Pencil01Icon,
  RefreshCw01Icon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { useContext, useState } from 'react';

import { Message as BaseMessage } from '@/components/Message';
import { IconButton, Text } from '@/design-system';
import {
  getMessageContent,
  getMessageFields,
  getMessageType,
} from '@/utils/messages';
import { cn } from '@/utils/tailwind';

import { RenderModeContext } from '../src/control/state';
import { FilesPreview } from './FilesPreview';
import { useChatContext } from './hooks/useChatContext';
import { extractFileContentParts, extractMessageContentParts } from './utils';

export const Message = ({ message }: { message: TMessage }) => {
  switch (message.type) {
    case 'ai':
      return <AIMessage message={message} />;
    case 'tool':
      return (
        <div className="">
          <Base message={message} />
        </div>
      );
    case 'human':
      return <HumanMessage message={message} />;
    default:
      return null;
  }
};

const AIMessage = ({ message }: { message: TMessage }) => {
  const { getMessagesMetadata, submit, setBranch } = useChatContext();
  const meta = getMessagesMetadata(message);
  const { firstSeenState, branch, branchOptions } = meta ?? {};
  const { parent_checkpoint: parentCheckpoint } = firstSeenState ?? {};

  const submitRegenerate = () => {
    submit(undefined, {
      checkpoint: parentCheckpoint,
    });
  };

  return (
    <div className="group flex w-fit flex-col gap-1">
      <Base message={message} />
      <div className="flex flex-row items-center justify-start gap-2">
        <IconButton
          icon={RefreshCw01Icon}
          label="Regenerate"
          color="secondary"
          variant="plain"
          className="invisible bg-transparent text-sm text-secondary group-hover:visible"
          onClick={submitRegenerate}
        />
        <BranchSwitcher
          branch={branch}
          branchOptions={branchOptions}
          onSelect={setBranch}
        />
      </div>
    </div>
  );
};

const HumanMessage = ({ message }: { message: TMessage }) => {
  const { getMessagesMetadata, submit, setBranch } = useChatContext();
  const meta = getMessagesMetadata(message);
  const { firstSeenState, branch, branchOptions } = meta ?? {};
  const { parent_checkpoint: parentCheckpoint } = firstSeenState ?? {};
  const [isEditing, setIsEditing] = useState(false);
  const initialMessageContent = getMessageContent(message);
  const initialMessageStringContent =
    typeof initialMessageContent === 'string' || !initialMessageContent
      ? initialMessageContent
      : extractMessageContentParts(initialMessageContent)?.[0]?.text;
  const initialMessageFileContent =
    typeof initialMessageContent === 'string' || !initialMessageContent
      ? []
      : extractFileContentParts(initialMessageContent);
  const [messageContent, setMessageContent] = useState(
    initialMessageStringContent
  );
  const [messageFiles, setMessageFiles] = useState(initialMessageFileContent);

  const submitEdit = () => {
    if (messageContent === initialMessageContent || !messageContent) {
      setIsEditing(false);
      return;
    }
    submit(
      {
        messages: [
          {
            type: 'human',
            content: [
              {
                type: 'text',
                text: messageContent,
              },
              ...(messageFiles ?? []),
            ],
          },
        ],
      },
      {
        checkpoint: parentCheckpoint,
      }
    );
    setIsEditing(false);
  };

  const messageEditor = (
    <div className="flex flex-col gap-2">
      <textarea
        value={messageContent}
        onChange={(e) => setMessageContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && e.metaKey) {
            submitEdit();
          }
        }}
        autoFocus
        className="rounded-md bg-transparent p-2 text-sm outline-none focus:border-brand"
      />
      <FilesPreview
        messageContentParts={messageFiles}
        handleDeleteFile={({ index }) => {
          setMessageFiles((prev) => prev.filter((_, i) => i !== index));
        }}
      />
    </div>
  );

  return (
    <div
      className={cn('ml-auto flex flex-col gap-1', !isEditing && 'group w-fit')}
    >
      {isEditing ? messageEditor : <Base message={message} />}
      {isEditing ? (
        <div className="flex flex-row items-center justify-end gap-2">
          <IconButton
            icon={XIcon}
            label="Cancel"
            onClick={() => setIsEditing(false)}
            color="secondary"
            variant="plain"
            className="bg-transparent"
          />
          <IconButton
            icon={ArrowRightIcon}
            label="Submit"
            onClick={submitEdit}
            color="secondary"
            variant="plain"
            className="bg-transparent"
          />
        </div>
      ) : (
        <div className="flex flex-row items-center justify-end gap-2">
          <BranchSwitcher
            branch={branch}
            branchOptions={branchOptions}
            onSelect={setBranch}
          />
          <IconButton
            icon={Pencil01Icon}
            label="Edit"
            onClick={() => setIsEditing(true)}
            color="secondary"
            variant="plain"
            className="bg-transparent"
          />
        </div>
      )}
    </div>
  );
};

const Base = ({ message }: { message: TMessage }) => {
  const { renderPrettyOptions } = useContext(RenderModeContext);
  const content = getMessageContent(message);
  const stringContent =
    typeof content === 'string' || !content
      ? content
      : extractMessageContentParts(content)?.[0]?.text;
  const files =
    typeof content === 'string' || !content
      ? []
      : extractFileContentParts(content);

  return (
    <div className="flex flex-col gap-1">
      <BaseMessage
        onEditKwargs={undefined}
        role={getMessageType(message) ?? 'HUMAN'}
        content={stringContent ?? ''}
        fields={getMessageFields(message)}
        name={getMessageFields(message)?.name}
        omitMaxHeight
        enableMarkdown={renderPrettyOptions.allow.includes('markdown')}
      />
      <FilesPreview messageContentParts={files} />
    </div>
  );
};

const BranchSwitcher = ({
  branch,
  branchOptions,
  onSelect,
}: {
  branch: string | undefined;
  branchOptions: string[] | undefined;
  onSelect: (branch: string) => void;
}) => {
  if (!branchOptions || !branch) return null;
  const index = branchOptions.indexOf(branch);

  return (
    <div className="invisible flex items-center gap-1 group-hover:visible">
      <IconButton
        icon={ChevronLeftIcon}
        label="Previous branch"
        onClick={() => {
          const prevBranch = branchOptions[index - 1];
          if (!prevBranch) return;
          onSelect(prevBranch);
        }}
        disabled={index === 0}
        color="secondary"
        variant="plain"
        className="bg-transparent"
        tooltipProps={{
          side: 'left',
        }}
      />
      <Text variant="sm" className="text-secondary">
        {index + 1} / {branchOptions.length}
      </Text>
      <IconButton
        icon={ChevronRightIcon}
        label="Next branch"
        onClick={() => {
          const nextBranch = branchOptions[index + 1];
          if (!nextBranch) return;
          onSelect(nextBranch);
        }}
        disabled={index === branchOptions.length - 1}
        color="secondary"
        variant="plain"
        className="bg-transparent"
        tooltipProps={{
          side: 'right',
        }}
      />
    </div>
  );
};
